import { Link } from "react-router-dom";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Clock,
  Users,
  BookOpen,
  Play,
  ArrowRight,
  Globe,
  GraduationCap,
} from "lucide-react";
import { LMSCourse } from "@jambushrushti/hooks/useLMS";

interface CourseCardProps {
  course: LMSCourse;
  showEnrollButton?: boolean;
  onEnroll?: (course: LMSCourse) => void;
  isEnrolled?: boolean;
}

export function CourseCard({
  course,
  showEnrollButton = true,
  onEnroll,
  isEnrolled = false,
}: CourseCardProps) {
  const getModeColor = (mode: string) => {
    switch (mode.toLowerCase()) {
      case "online":
        return "bg-emerald-500/10 text-emerald-700 border-emerald-200";
      case "offline":
        return "bg-blue-500/10 text-blue-700 border-blue-200";
      case "hybrid":
        return "bg-violet-500/10 text-violet-700 border-violet-200";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case "beginner":
        return "bg-primary/10 text-primary border-primary/20";
      case "intermediate":
        return "bg-amber-500/10 text-amber-700 border-amber-200";
      case "advanced":
        return "bg-rose-500/10 text-rose-700 border-rose-200";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Card className="group overflow-hidden rounded-2xl border-2 border-border/50 bg-card transition-all duration-500 hover:border-primary/30 hover:shadow-xl">
      {/* Thumbnail */}
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10">
        {course.thumbnail_url ? (
          <img
            src={course.thumbnail_url}
            alt={course.title}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10">
              <BookOpen className="h-10 w-10 text-primary/50" />
            </div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

        {/* Mode Badge */}
        <Badge
          className={`absolute left-4 top-4 ${getModeColor(course.course_mode)} font-medium shadow-sm`}
        >
          {course.course_mode}
        </Badge>

        {/* Enrolled Badge */}
        {isEnrolled && (
          <Badge className="absolute right-4 top-4 bg-green-500 font-medium text-white shadow-sm">
            Enrolled
          </Badge>
        )}

        {/* Play overlay on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-500 group-hover:opacity-100">
          <div className="flex h-16 w-16 scale-75 transform items-center justify-center rounded-full bg-card/95 shadow-xl transition-transform duration-500 group-hover:scale-100">
            <Play className="ml-1 h-7 w-7 text-primary" />
          </div>
        </div>
      </div>

      <CardContent className="space-y-4 p-5">
        {/* Subject & Level */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="border-border text-xs font-medium"
          >
            {course.subject}
          </Badge>
          <Badge
            className={`text-xs font-medium ${getLevelColor(course.level)}`}
          >
            {course.level}
          </Badge>
        </div>

        {/* Title */}
        <h3 className="font-heading line-clamp-2 text-lg font-bold leading-tight text-foreground transition-colors duration-300 group-hover:text-primary">
          {course.title}
        </h3>

        {/* Description */}
        {course.description && (
          <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
            {course.description}
          </p>
        )}

        {/* Divider */}
        <div className="h-px bg-border/50" />

        {/* Meta info */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            {course.instructor_name && (
              <div className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-primary/70" />
                <span className="max-w-[100px] truncate font-medium">
                  {course.instructor_name}
                </span>
              </div>
            )}
            {course.duration && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-primary/70" />
                <span>{course.duration}</span>
              </div>
            )}
          </div>
          {course.language && (
            <div className="flex items-center gap-1">
              <Globe className="h-3.5 w-3.5" />
              <span className="text-xs">{course.language}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {showEnrollButton && (
          <div className="mt-2 flex gap-2">
            <Button
              asChild
              variant="outline"
              className="h-11 flex-1 rounded-xl"
            >
              <Link
                to={`/learning/courses/${course.id}`}
                className="flex items-center justify-center gap-2"
              >
                View Details
              </Link>
            </Button>
            {isEnrolled ? (
              <Button
                asChild
                className="h-11 flex-1 rounded-xl bg-green-600 hover:bg-green-700"
              >
                <Link
                  to="/learning/my-courses"
                  className="flex items-center justify-center gap-2"
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <Button
                onClick={() => onEnroll?.(course)}
                className="h-11 flex-1 rounded-xl bg-primary hover:bg-primary/90"
              >
                <GraduationCap className="mr-2 h-4 w-4" />
                Enroll
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
